import { MAX_FILE_BYTES, UPLOAD_PART_BYTES } from '@/protocol/limits';
import { db } from '@/store/db';
import { bytesToHex, newOperationId, type Hex } from '@/store/ids';
import type { StoredAttachment, UploadRecord } from '@/store/types';
import { computeWaveform } from '@/media/waveform';

/**
 * Turning a file the user chose into a durable transfer record.
 *
 * The record is written before anything touches the network — the same rule the
 * outbox follows for messages, and for the same reason: a transfer the user
 * believes has started must survive a reload, and the only way to guarantee
 * that is for the bytes and the intent to be in IndexedDB first.
 *
 * The SHA-256 is computed here rather than in the worker because `CreateUpload`
 * needs it in its first request, and because hashing a multi-gigabyte file is
 * the one place where doing the work up front is visibly better than
 * discovering halfway through an upload that the digest cannot be produced.
 */

export class AttachmentTooLargeError extends Error {
  constructor() {
    super('the file exceeds the 4 GiB object limit');
    this.name = 'AttachmentTooLargeError';
  }
}

/**
 * The `media_kind` Section 15 sends with `CreateUpload`.
 *
 * It is derived from the declared MIME type, which is a *hint*: Section 06 is
 * explicit that the server detects the real type from the bytes and that a
 * declared MIME never authorizes inline execution. Getting this wrong therefore
 * costs a less specific preview, not a security property.
 */
export function mediaKindFor(mime: string, override?: string): string {
  if (override) {
    return override;
  }
  if (mime.startsWith('image/')) {
    return 'photo';
  }
  if (mime.startsWith('video/')) {
    return 'video';
  }
  if (mime.startsWith('audio/')) {
    return 'voice';
  }
  return 'file';
}

async function sha256OfBlob(blob: Blob): Promise<Hex> {
  if (!crypto.subtle) {
    throw new Error('Web Crypto is unavailable; the client requires a secure context.');
  }
  // `arrayBuffer()` materializes the whole file. `crypto.subtle.digest` has no
  // streaming form, so there is no alternative within the frozen dependency
  // set; the 4 GiB object limit is the real bound on what this can allocate,
  // and the browser refuses the allocation rather than corrupting anything.
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return bytesToHex(new Uint8Array(digest));
}

/**
 * Reads the intrinsic dimensions or duration of a media file.
 *
 * Section 14: "Image dimensions and media duration use verified metadata to
 * avoid unstable layout." Verified here means measured from the decoded object
 * rather than taken from a filename or a container header the client did not
 * parse. A failure returns nulls, and the layout falls back to a fixed-ratio
 * placeholder instead of guessing.
 */
async function probe(blob: Blob, kind: string): Promise<{
  width: number | null;
  height: number | null;
  durationMs: number | null;
}> {
  const url = URL.createObjectURL(blob);
  try {
    if (kind === 'photo') {
      const image = await loadElement(new Image(), url);
      return { width: image.naturalWidth, height: image.naturalHeight, durationMs: null };
    }
    if (kind === 'video' || kind === 'video_note') {
      const video = document.createElement('video');
      video.preload = 'metadata';
      const loaded = await loadElement(video, url);
      return {
        width: loaded.videoWidth,
        height: loaded.videoHeight,
        durationMs: Number.isFinite(loaded.duration) ? Math.round(loaded.duration * 1000) : null,
      };
    }
    if (kind === 'voice') {
      const audio = document.createElement('audio');
      audio.preload = 'metadata';
      const loaded = await loadElement(audio, url);
      return {
        width: null,
        height: null,
        durationMs: Number.isFinite(loaded.duration) ? Math.round(loaded.duration * 1000) : null,
      };
    }
    return { width: null, height: null, durationMs: null };
  } catch {
    return { width: null, height: null, durationMs: null };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadElement<T extends HTMLImageElement | HTMLVideoElement | HTMLAudioElement>(
  element: T,
  url: string,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const done = (): void => {
      resolve(element);
    };
    if (element instanceof HTMLImageElement) {
      element.onload = done;
    } else {
      element.onloadedmetadata = done;
    }
    element.onerror = () => {
      reject(new Error('could not read media metadata'));
    };
    element.src = url;
  });
}

export interface CreateUploadInput {
  conversationId: Hex;
  file: Blob;
  filename: string;
  /** Overrides the MIME-derived kind, for a voice or video note. */
  mediaKind?: string;
  /** Known duration for a recording, so a failed probe does not lose it. */
  durationMs?: number;
}

/**
 * Creates the local transfer row for one attachment.
 *
 * Nothing is sent here. The caller asks the worker to start the transfer once
 * the row exists, so that a failure to persist never produces a transfer with
 * no record of itself.
 */
export async function createUploadRecord(input: CreateUploadInput): Promise<UploadRecord> {
  if (input.file.size > MAX_FILE_BYTES) {
    throw new AttachmentTooLargeError();
  }

  const mime = input.file.type || 'application/octet-stream';
  const mediaKind = mediaKindFor(mime, input.mediaKind);
  const [sha256, measured] = await Promise.all([
    sha256OfBlob(input.file),
    probe(input.file, mediaKind),
  ]);

  // A locally computed waveform is only for the review step of a recording;
  // the authoritative 100 samples come from the media worker.
  const waveform = mediaKind === 'voice' ? await computeWaveform(input.file) : null;

  const now = Date.now();
  const record: UploadRecord = {
    uploadId: newOperationId(),
    serverUploadId: null,
    mediaId: null,
    conversationId: input.conversationId,
    filename: input.filename,
    mime,
    mediaKind,
    sizeBytes: input.file.size,
    sha256,
    partSize: UPLOAD_PART_BYTES,
    state: 'CREATED',
    bytesConfirmed: 0,
    parts: [],
    expiresAt: 0,
    createOpId: newOperationId(),
    completeOpId: newOperationId(),
    cancelOpId: newOperationId(),
    lastError: null,
    blob: input.file,
    width: measured.width,
    height: measured.height,
    durationMs: measured.durationMs ?? input.durationMs ?? waveform?.durationMs ?? null,
    waveform: waveform?.samples ?? null,
    createdAt: now,
    updatedAt: now,
  };

  await db.uploads.put(record);
  return record;
}

/** The attachment reference a READY upload contributes to a message. */
export function attachmentFromUpload(record: UploadRecord): StoredAttachment | null {
  if (record.state !== 'READY' || record.mediaId === null) {
    return null;
  }
  return {
    mediaId: record.mediaId,
    caption: null,
    filename: record.filename,
    mime: record.mime,
    sizeBytes: record.sizeBytes,
    width: record.width,
    height: record.height,
    durationMs: record.durationMs,
  };
}

/**
 * Whether a MIME type has a native player or renderer in the browser.
 *
 * Section 14: "Unknown formats show filename, size, and download action. Never
 * claim an arbitrary file is previewable." So this list is deliberately short
 * and allow-list shaped, and Section 06's rule that HTML and SVG download as
 * attachments rather than running inside the application origin is enforced by
 * their simple absence from it.
 */
export function isPreviewable(mime: string): boolean {
  const lower = mime.toLowerCase();
  if (lower === 'image/svg+xml') {
    return false;
  }
  return (
    lower === 'image/jpeg' ||
    lower === 'image/png' ||
    lower === 'image/webp' ||
    lower === 'image/gif' ||
    lower === 'video/mp4' ||
    lower === 'audio/mpeg' ||
    lower === 'audio/mp4' ||
    lower === 'audio/aac' ||
    lower === 'audio/webm' ||
    lower === 'audio/ogg'
  );
}
